import { buildCsvDocument } from './csvUtils';
import {
  ALL_STAMMDATEN_EXPORT_FIELD_KEYS,
  formatEmployeeExportCell,
  resolveExportFields,
  ZERO_MEANS_EMPTY_NUMBER_FIELDS,
  type StammdatenExportField,
  type StammdatenExportFieldKey,
} from './employeeStammdatenExport';

/**
 * Änderungsprotokoll lohnrelevanter Mitarbeiter-Stammdaten.
 * Pure Logik (Feldkatalog, Normalisierung, Diff, CSV) — wird von Server und Client genutzt.
 */

export type MasterDataChangeKind = 'create' | 'update' | 'delete';

/** Normalisierter Rohwert, wie er als jsonb gespeichert wird. */
export type MasterDataValue = string | number | boolean | null;

export type MasterDataFieldChange = {
  field_key: StammdatenExportFieldKey;
  old_value: MasterDataValue;
  new_value: MasterDataValue;
};

/** Platzhalter-Feld für Löschzeilen. */
export const MASTER_DATA_DELETE_FIELD_KEY = 'employee';

const NOT_PAYROLL_RELEVANT = new Set<StammdatenExportFieldKey>([
  'work_area',
  'driving_license_classes',
  'emergency_contact_name',
  'emergency_contact_relation',
  'emergency_contact_phone',
]);

/** Getrackte Felder in Katalogreihenfolge (persönlich, Beruf, Finanzen, Meldedaten, Vergütung). */
export const PAYROLL_RELEVANT_FIELD_KEYS: StammdatenExportFieldKey[] = ALL_STAMMDATEN_EXPORT_FIELD_KEYS.filter(
  (key) => !NOT_PAYROLL_RELEVANT.has(key),
);

const FIELD_BY_KEY = new Map<StammdatenExportFieldKey, StammdatenExportField>(
  resolveExportFields(PAYROLL_RELEVANT_FIELD_KEYS).map((field) => [field.key, field]),
);

export function isPayrollRelevantFieldKey(key: string): key is StammdatenExportFieldKey {
  return FIELD_BY_KEY.has(key as StammdatenExportFieldKey);
}

export function masterDataFieldLabel(key: string): string {
  if (key === MASTER_DATA_DELETE_FIELD_KEY) return 'Mitarbeiter';
  return FIELD_BY_KEY.get(key as StammdatenExportFieldKey)?.label ?? key;
}

function toFiniteNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const parsed = Number.parseFloat(String(raw).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function toBoolean(raw: unknown): boolean {
  if (typeof raw === 'boolean') return raw;
  const s = String(raw ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'ja';
}

/**
 * Bringt einen Rohwert aus DB oder Request auf die Vergleichsform:
 * leer/`null` → `null`, Zahlen → `number` (0 zählt bei Betragsfeldern wie leer),
 * Datum → `YYYY-MM-DD`, Booleans → `boolean`, Text getrimmt.
 */
export function normalizeMasterDataValue(key: StammdatenExportFieldKey, raw: unknown): MasterDataValue {
  const field = FIELD_BY_KEY.get(key);
  if (!field) return null;
  switch (field.kind) {
    case 'number': {
      const n = toFiniteNumber(raw);
      if (n === null) return null;
      if (n === 0 && ZERO_MEANS_EMPTY_NUMBER_FIELDS.has(key)) return null;
      return n;
    }
    case 'boolean':
      return toBoolean(raw);
    case 'date': {
      const s = String(raw ?? '').trim();
      if (!s) return null;
      return s.slice(0, 10);
    }
    default: {
      const s = String(raw ?? '').trim();
      return s ? s : null;
    }
  }
}

/** Leer im Sinne der Neuanlage: nichts erfasst. */
function isEmptyValue(value: MasterDataValue): boolean {
  return value === null || value === false;
}

/** Feldgenaue Unterschiede zwischen zwei Mitarbeiter-Zeilen, nur lohnrelevante Felder. */
export function diffEmployeeMasterData(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): MasterDataFieldChange[] {
  const changes: MasterDataFieldChange[] = [];
  for (const key of PAYROLL_RELEVANT_FIELD_KEYS) {
    const oldValue = normalizeMasterDataValue(key, before[key]);
    const newValue = normalizeMasterDataValue(key, after[key]);
    if (oldValue === newValue) continue;
    changes.push({ field_key: key, old_value: oldValue, new_value: newValue });
  }
  return changes;
}

/** Neuanlage: eine Zeile je befülltem lohnrelevantem Feld (alt = leer). */
export function buildCreateChanges(after: Record<string, unknown>): MasterDataFieldChange[] {
  const changes: MasterDataFieldChange[] = [];
  for (const key of PAYROLL_RELEVANT_FIELD_KEYS) {
    const newValue = normalizeMasterDataValue(key, after[key]);
    if (isEmptyValue(newValue)) continue;
    changes.push({ field_key: key, old_value: null, new_value: newValue });
  }
  return changes;
}

/** Anzeigeform eines gespeicherten Werts (dt. Datum, Komma-Zahl, Enum-Label, Ja/Nein, IBAN gruppiert). */
export function formatMasterDataValue(key: string, value: MasterDataValue): string {
  if (value === null || value === undefined) return '';
  const field = FIELD_BY_KEY.get(key as StammdatenExportFieldKey);
  if (!field) return String(value);
  return formatEmployeeExportCell({ [key]: value }, field);
}

export function masterDataChangeKindLabel(kind: MasterDataChangeKind): string {
  if (kind === 'create') return 'Neuanlage';
  if (kind === 'delete') return 'Löschung';
  return 'Änderung';
}

export const MASTER_DATA_EXPORT_TIME_ZONE = 'Europe/Berlin';

export function formatMasterDataChangeDate(iso: string, timeZone: string = MASTER_DATA_EXPORT_TIME_ZONE): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatMasterDataChangeTime(iso: string, timeZone: string = MASTER_DATA_EXPORT_TIME_ZONE): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { timeZone, hour: '2-digit', minute: '2-digit' });
}

export type MasterDataChangeCsvRow = {
  personal_number: string;
  last_name: string;
  first_name: string;
  change_kind: MasterDataChangeKind;
  field_key: string;
  old_value: MasterDataValue;
  new_value: MasterDataValue;
  /** ISO-Zeitpunkt (UTC). */
  changed_at: string;
  changed_by_name: string;
};

export const MASTER_DATA_CHANGES_CSV_HEADER = [
  'Pers.-Nr.',
  'Nachname',
  'Vorname',
  'Datum',
  'Uhrzeit',
  'Art',
  'Feld',
  'Alter Wert',
  'Neuer Wert',
  'Geändert von',
];

function compareDe(a: string, b: string): number {
  return a.localeCompare(b, 'de', { sensitivity: 'base' });
}

/** Sortiert Nachname → Vorname → Zeitpunkt; Semikolon-CSV mit BOM wie die Agenda-Dateien. */
export function buildMasterDataChangesCsv(rows: MasterDataChangeCsvRow[]): string {
  const sorted = [...rows].sort(
    (a, b) =>
      compareDe(a.last_name, b.last_name) ||
      compareDe(a.first_name, b.first_name) ||
      a.changed_at.localeCompare(b.changed_at),
  );
  const dataRows = sorted.map((row) => [
    row.personal_number,
    row.last_name,
    row.first_name,
    formatMasterDataChangeDate(row.changed_at),
    formatMasterDataChangeTime(row.changed_at),
    masterDataChangeKindLabel(row.change_kind),
    masterDataFieldLabel(row.field_key),
    formatMasterDataValue(row.field_key, row.old_value),
    formatMasterDataValue(row.field_key, row.new_value),
    row.changed_by_name,
  ]);
  return buildCsvDocument(MASTER_DATA_CHANGES_CSV_HEADER, dataRows);
}

export function masterDataChangesExportFilename(from: string, to: string): string {
  return `stammdatenaenderungen_${from}_${to}.csv`;
}
